import { Labeled } from 'components/labeled/labeled';
import { React, useEffect, useState } from 'react';
import { map } from 'lodash-es';
import { MenuList } from 'react-menu-list';
import { useLocation, useNavigate } from 'react-router-dom';
import { GenericInput } from 'components/genericInput/genericInput';
import { MenuItem } from 'components/menuItem/menuItem';
import { MenuItemCard } from 'components/menuItemCard/menuItemCard';
import { MenuAction } from 'components/menuAction/menuAction';
import { MenuDivider } from 'components/menuDivider/menuDivider';
import { Avatar, avatarOptions } from 'components/avatar/avatar';
import { MenuScrollable } from 'components/menuScrollable/menuScrollable';
import { MenuSlot } from 'components/menuSlot/menuSlot';
import { NavBar } from 'components/nav/navBar';
import { NavItem } from 'components/nav/navItem';
import { NavSegment } from 'components/nav/navSegment';
import { SearchButton } from 'components/searchButton/searchButton';
import { Section } from 'components/section/section';
import { routesPath } from 'constants/routesPath';
import { siteLinks } from 'constants/siteLinks';
import clients from '../../redux/modules/clients';
import { MdOutlineSearch as SearchIcon } from 'react-icons/md';
import { AiOutlineCheck as CheckIcon } from 'react-icons/ai';
import { BiCalendarEvent as CalendarIcon, BiMusic as MusicIcon } from 'react-icons/bi';
import { IoMdMailOpen as MailIcon } from 'react-icons/io';
import { Button } from 'components/button/button';
import { useDispatch, useSelector } from 'react-redux';
import { baseSliceWithTags } from '../../redux/baseSlice';
import storageService from '../../services/storageService';
import { NavToggle } from 'components/nav/navToggle';
import styles from './navContainer.module.scss';
import classNames from 'classnames';
import { handleNavToggle } from '../../redux/common/commonSlice';
import { apiUrls } from '../../constants/apiurls';
import { NavbarMobile } from '@components/nav/navMobile';
import { appUrl } from 'constants/baseUrl';
import { ClientDropdownWithProjects } from 'components/clientDropdownWithProjects/ClientDropdownWithProjects';
import { fetchAutoCompleteData } from '../../services/autoCompleteService';
import { useGetPendingClaimsQuery } from '../../redux/modules/clientOwnership';
import tmrAccounts from '../../redux/modules/tmrAccounts';
import { useCreateInternalSsoMutation } from '../../redux/apiService/auth';
import { buildInternalSsoHandoffUrl } from '../../services/internalSsoService';
import toast from 'react-hot-toast';
import { signOutEverywhere } from '../../services/keycloakService';

export const NavContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [clientSearchQuery, setClientSearchQuery] = useState(null);
  const [colors, setColors] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [createInternalSso, createInternalSsoState] = useCreateInternalSsoMutation();

  const { data: pendingClaimsData } = useGetPendingClaimsQuery();

  const { data: tmrAccountData } = tmrAccounts
    .list()
    .useTmrAccountdetailQuery({ extendedPath: '/current' });
  const accountLevel = tmrAccountData?.data?.account?.account_level;
  const isPaidAccount = accountLevel && accountLevel !== 'free';

  const {
    data: clientData,
    isLoading: isLoadingClient,
  } = clients
    .list()
    .useClientslistQuery(
      { params: '?with_projects=1', extendedPath: apiUrls.getClientListing() },
      { refetchOnMountOrArgChange: true },
    );

  const { data: searchedClients } = clients.list().useClientslistQuery(
    { params: '?with_projects=1', extendedPath: apiUrls.getClientListing() },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => ({
        data: data?.data?.filter((el) => el.title.toUpperCase().includes(clientSearchQuery)),
      }),
    },
  );

  const userInfo = JSON.parse(storageService.getUserInfo());

  // Pending claims notifications
  const pendingClaims = pendingClaimsData?.data || [];
  const lastSeen = parseInt(localStorage.getItem('tmr_notif_last_seen') || '0', 10);
  const unreadCount = pendingClaims.filter(
    (c) => new Date(c.created).getTime() > lastSeen
  ).length;

  const markNotificationsRead = () => {
    localStorage.setItem('tmr_notif_last_seen', Date.now().toString());
    setNotifOpen(false);
  };

  const authHandler = async () => {
    dispatch(baseSliceWithTags.util.resetApiState());
    // HOLM-506: a Holedo sign-in session is ended for every Holedo service.
    const redirected = await signOutEverywhere();
    if (!redirected) navigate(routesPath.login, { replace: false });
  };

  const goToSpaces = async () => {
    try {
      const response = await createInternalSso({
        target: 'flutter',
        returnPath: '/spaces',
      }).unwrap();
      window.location.assign(
        buildInternalSsoHandoffUrl(response.data.target_url, response.data.code),
      );
    } catch (error) {
      const responseData = error?.data;
      const serverMessage = Array.isArray(responseData?.errors)
        ? responseData.errors[0]
        : responseData?.errors || responseData?.messages;
      toast.error(serverMessage || 'Could not open Spaces. Please try again.');
    }
  };

  const goToMeet = async () => {
    try {
      const response = await createInternalSso({
        target: 'flutter',
        returnPath: '/meet',
      }).unwrap();
      window.location.assign(
        buildInternalSsoHandoffUrl(response.data.target_url, response.data.code),
      );
    } catch (error) {
      const responseData = error?.data;
      const serverMessage = Array.isArray(responseData?.errors)
        ? responseData.errors[0]
        : responseData?.errors || responseData?.messages;
      toast.error(serverMessage || 'Could not open Meet. Please try again.');
    }
  };

  const isNavMobile = useSelector((state) => state.commonReducer.navBarToggle);

  useEffect(() => {
    const getColors = async () => {
      const colrs = await fetchAutoCompleteData('', apiUrls.getColors());
      setColors(colrs);
    };
    getColors();
  }, []);

  return (
    <>
      <div
        className={classNames(styles.navigation, styles.mainNav)}
        style={{ position: 'fixed', width: '100%', zIndex: 99, top: 0, left: 0 }}
      >
        <NavBar type="mainmenu">
          <NavSegment placement="left" style={{ position: 'relative', zIndex: 10000 }}>
            <NavToggle />
            <NavItem
              link={routesPath.dashboard}
              onClick={() => {
                dispatch(handleNavToggle(false));
              }}
            >
              Dashboard
            </NavItem>
            <NavItem
              zIndex={23}
              title={<Labeled toCount={clientData?.data}>Clients</Labeled>}
              hasDropdown
              active={location.pathname.includes(routesPath.clientFormPage)}
              link=""
            >
              <MenuSlot onSelected={(e) => e.stopPropagation()}>
                <GenericInput
                  type="text"
                  placeholder={'Search'}
                  iconRight
                  value={clientSearchQuery || ''}
                  onChange={(event) => setClientSearchQuery(event.target.value)}
                />
              </MenuSlot>
              <MenuScrollable>
                <MenuList>
                  {(clientSearchQuery ? searchedClients?.data : clientData?.data)?.map((client) => (
                    <MenuItemCard
                      key={`client-${client.id}`}
                      name={client.title}
                      avatar={{ ...avatarOptions(client, 'client'), size: 30 }}
                      color={client.colour?.hex || '#FFD600'}
                      link={routesPath.getClientProjects(client.id)}
                      onClick={() => {
                        navigate(routesPath.getClientProjects(client.id));
                        dispatch(handleNavToggle(false));
                      }}
                    />
                  ))}
                </MenuList>
              </MenuScrollable>
              <MenuDivider />
              <MenuList>
                <MenuAction
                  onClick={() => {
                    navigate(routesPath.clientFormPage, { replace: false });
                    dispatch(handleNavToggle(false));
                  }}
                >
                  Add new client
                </MenuAction>
              </MenuList>
            </NavItem>
            <NavItem
              link={routesPath.jobs}
              onClick={() => {
                dispatch(handleNavToggle(false));
              }}
            >
              Job posts
            </NavItem>
            <NavItem
              zIndex={23}
              title={<Labeled toCount={clientData?.data}>Projects</Labeled>}
              hasDropdown
              active={location.pathname.includes(routesPath.clientFormPage)}
              link=""
            >
              <MenuSlot onSelected={(e) => e.stopPropagation()}>
                <GenericInput
                  type="text"
                  placeholder={'Search'}
                  iconRight
                  value={clientSearchQuery || ''}
                  onChange={(event) => setClientSearchQuery(event.target.value)}
                />
              </MenuSlot>
              <MenuScrollable>
                <MenuList>
                  {(clientSearchQuery ? searchedClients?.data : clientData?.data)?.map((client) => (
                    <ClientDropdownWithProjects
                      key={`client-dropdown-${client.id}`}
                      client={client}
                      colors={colors}
                      onNavigate={() => dispatch(handleNavToggle(false))}
                    />
                  ))}
                </MenuList>
              </MenuScrollable>
            </NavItem>
            <NavItem
              onClick={() => {
                dispatch(handleNavToggle(false));
              }}
              link={routesPath.recruitersList()}
            >
              Recruiters
            </NavItem>
            {isPaidAccount && (
              <NavItem
                onClick={() => {
                  dispatch(handleNavToggle(false));
                }}
                link={routesPath.discoverClients}
              >
                Discover
              </NavItem>
            )}
            <NavItem
              onClick={() => {
                dispatch(handleNavToggle(false));
              }}
              link={routesPath.pipelineList()}
            >
              Settings
            </NavItem>
          </NavSegment>
          <NavSegment placement="user">
            <NavItem>
              <GenericInput
                name="keyword"
                type="text"
                placeholder="Search Account"
                icon={<SearchIcon />}
                iconRight
                navSearch
              // value={searchQuery}
              // onChange={(event) => onSearch(event.target.value)}
              />
            </NavItem>
            <NavItem>
              <Button style={{ paddingInline: 10 + 'px' }}>
                <CheckIcon size={20} />
              </Button>
            </NavItem>
            <NavItem>
              <a
                href={siteLinks.music}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Music"
                title="Music"
              >
                <MusicIcon size={20} />
              </a>
            </NavItem>
            <NavItem>
              <Button style={{ paddingInline: 10 + 'px' }}>
                <CalendarIcon size={20} />
              </Button>
            </NavItem>
            <NavItem
              zIndex={23}
              hasDropdown
              toRight
              title={
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <MailIcon size={20} />
                  {unreadCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -6,
                        right: -8,
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        borderRadius: 9,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 4px',
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </div>
              }
            >
              {pendingClaims.length === 0 ? (
                <MenuSlot>
                  <span style={{ padding: '12px 16px', display: 'block', color: '#6b7280', fontSize: '0.85rem' }}>
                    No new notifications
                  </span>
                </MenuSlot>
              ) : (
                <MenuScrollable>
                  <MenuList>
                    {pendingClaims.map((claim) => (
                      <MenuItemCard
                        key={claim.id}
                        name={claim.client?.title || `Client #${claim.client_id}`}
                        subhead={
                          claim.notification_type === 'reassignment'
                            ? 'Client assigned to you'
                            : claim.notification_type === 'claim_review'
                              ? `Claim needs Holedo review (Account #${claim.new_owner_account_id})`
                              : `Claim request from Account #${claim.new_owner_account_id}`
                        }
                        link={routesPath.editClient(claim.client_id)}
                        onClick={() => {
                          navigate(routesPath.editClient(claim.client_id));
                          markNotificationsRead();
                          dispatch(handleNavToggle(false));
                        }}
                      />
                    ))}
                  </MenuList>
                </MenuScrollable>
              )}
              <MenuDivider />
              <MenuList>
                <MenuAction onClick={markNotificationsRead}>
                  Mark all as read
                </MenuAction>
              </MenuList>
            </NavItem>
            <NavItem
              zIndex={23}
              className={classNames(styles.navAvatar, { [styles.mobileNavActive]: isNavMobile })}
              hasDropdown
              toRight
              title={
                <Avatar src={JSON.parse(localStorage.getItem('userData'))?.avatar} size={30} />
              }
            >
              <MenuItem onClick={() => window.location.href = `${appUrl}/profile/${userInfo.slug}`}>Go to Profile</MenuItem>
              <MenuItem onClick={goToSpaces} disabled={createInternalSsoState.isLoading}>
                {createInternalSsoState.isLoading ? 'Opening Spaces...' : 'Go to Spaces'}
              </MenuItem>
              <MenuItem onClick={goToMeet} disabled={createInternalSsoState.isLoading}>
                {createInternalSsoState.isLoading ? 'Opening Meet...' : 'Meet'}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  dispatch(handleNavToggle(false));
                  authHandler();
                }}
              >
                Logout
              </MenuItem>
            </NavItem>
            <NavItem link={routesPath.searchCandidates()}>
              <SearchButton />
            </NavItem>
          </NavSegment>
        </NavBar>
      </div>
      <NavbarMobile />
    </>
  );
};
